// Profile.js - Fixed error handling
import { useState } from "react";
import Sidebar from "../components/Sidebar";
import api from "../services/api";
import "../styles/dashboard.css";

export default function Profile() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  
  const userName = localStorage.getItem("userName") || "User";
  const userRole = localStorage.getItem("role") || "User";
  const userEmail = localStorage.getItem("userEmail") || "user@example.com";

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: "error", text: "All fields are required!" });
      return;
    }
    
    if (newPassword.length < 6) {
      setMessage({ type: "error", text: "New password must be at least 6 characters long!" });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "New password and confirm password do not match!" });
      return;
    }
    
    setLoading(true);
    setMessage({ type: "", text: "" });
    
    try {
      const userId = localStorage.getItem("userId");
      
      // Using the correct endpoint: /api/users/{userId}/password with query params
      const response = await api.put(`/users/${userId}/password`, null, {
        params: {
          currentPassword: currentPassword,
          newPassword: newPassword
        }
      });
      
      // Success case
      if (response.data && response.data.success) {
        setMessage({ type: "success", text: "✅ Password changed successfully!" });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setShowPasswordChange(false);
        
        // Logout user after password change for security
        setTimeout(() => {
          const shouldLogout = window.confirm(
            "Password changed successfully!\n\nFor security reasons, you will be logged out. Please login again with your new password.\n\nClick OK to continue."
          );
          if (shouldLogout) {
            localStorage.clear();
            window.location.href = "/login";
          }
        }, 2000);
      } else {
        setMessage({ type: "error", text: response.data?.message || "Failed to change password" });
      }
    } catch (err) {
      console.error("Password change error:", err);
      
      // Handle different error response formats
      let errorMsg = "Failed to change password. Please try again.";
      
      if (err.response) {
        const errorData = err.response.data;
        
        // Check if error is a string
        if (typeof errorData === 'string') {
          errorMsg = errorData;
        }
        // Check if error has message property
        else if (errorData && errorData.message) {
          errorMsg = errorData.message;
        }
        // Check if error has error property (Spring Boot default error response)
        else if (errorData && errorData.error) {
          errorMsg = errorData.error;
        }
        // Handle Spring Boot error object with status, error, path
        else if (errorData && typeof errorData === 'object') {
          if (errorData.message) {
            errorMsg = errorData.message;
          } else if (errorData.error) {
            errorMsg = errorData.error;
          } else if (errorData.status === 401) {
            errorMsg = "Current password is incorrect";
          } else if (errorData.status === 404) {
            errorMsg = "User not found";
          } else if (errorData.status === 400) {
            errorMsg = "Invalid request. Please check your input.";
          } else {
            errorMsg = errorData.message || JSON.stringify(errorData);
          }
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setMessage({ type: "error", text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div className="header-title">
            <h1>Settings & Profile</h1>
            <p>Manage your account settings and security</p>
          </div>
          <div className="header-date">{new Date().toLocaleDateString()}</div>
        </div>

        <div className="profile-grid">
          {/* Profile Information Card */}
          <div className="profile-card">
            <h2>👤 Profile Information</h2>
            <div className="profile-avatar" style={{ textAlign: "center", marginBottom: "20px" }}>
              <div style={{
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #4361ee 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                fontSize: "48px",
                color: "white",
                boxShadow: "0 4px 14px rgba(0,0,0,0.1)"
              }}>
                {userName.charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="profile-row">
              <span className="label">Name:</span>
              <span>{userName}</span>
            </div>
            <div className="profile-row">
              <span className="label">Role:</span>
              <span style={{ fontWeight: "bold", color: "#4361ee" }}>{userRole?.replace('_', ' ')}</span>
            </div>
            <div className="profile-row">
              <span className="label">Email:</span>
              <span>{userEmail}</span>
            </div>
            <div className="profile-row">
              <span className="label">User ID:</span>
              <span>{localStorage.getItem("userId") || "N/A"}</span>
            </div>
          </div>

          {/* Security Settings Card */}
          <div className="profile-card">
            <h2>🔒 Security Settings</h2>
            
            {!showPasswordChange ? (
              <>
                <div className="security-info">
                  <p style={{ marginBottom: "20px", color: "#666", fontSize: "14px" }}>
                    Keep your account secure by changing your password regularly.
                  </p>
                  <button 
                    className="btn-primary" 
                    onClick={() => setShowPasswordChange(true)}
                    style={{ width: "100%" }}
                  >
                    🔐 Change Password
                  </button>
                </div>
                
                <div className="security-tips" style={{
                  marginTop: "20px",
                  padding: "15px",
                  background: "#f0fdf4",
                  borderRadius: "8px",
                  borderLeft: "4px solid #10b981"
                }}>
                  <h4 style={{ margin: "0 0 10px 0", color: "#065f46" }}>💡 Security Tips</h4>
                  <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "12px", color: "#065f46" }}>
                    <li>Use a strong password with at least 6 characters</li>
                    <li>Include numbers, symbols, and mixed case letters</li>
                    <li>Don't share your password with anyone</li>
                    <li>Change password every 90 days</li>
                    <li>Logout from shared devices after use</li>
                  </ul>
                </div>
              </>
            ) : (
              <form onSubmit={handlePasswordChange}>
                {message.text && (
                  <div style={{
                    padding: "10px",
                    borderRadius: "8px",
                    marginBottom: "15px",
                    background: message.type === "success" ? "#d1fae5" : "#fee2e2",
                    color: message.type === "success" ? "#065f46" : "#991b1b",
                    fontSize: "12px"
                  }}>
                    {message.text}
                  </div>
                )}
                
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                    Current Password *
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter your current password"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px"
                    }}
                    required
                  />
                </div>
                
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                    New Password *
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px"
                    }}
                    required
                  />
                  <small style={{ fontSize: "11px", color: "#666" }}>
                    Password must be at least 6 characters long
                  </small>
                </div>
                
                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                    Confirm New Password *
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px"
                    }}
                    required
                  />
                </div>
                
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowPasswordChange(false);
                      setCurrentPassword("");
                      setNewPassword("");
                      setConfirmPassword("");
                      setMessage({ type: "", text: "" });
                    }}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={loading}
                    style={{
                      flex: 1,
                      background: loading ? "#9ca3af" : "#10b981",
                      cursor: loading ? "not-allowed" : "pointer"
                    }}
                  >
                    {loading ? "Changing..." : "✅ Change Password"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Session Information Card */}
          <div className="profile-card">
            <h2>📱 Session Information</h2>
            <div className="session-info">
              <div className="profile-row">
                <span className="label">Login Time:</span>
                <span>{new Date().toLocaleString()}</span>
              </div>
              <div className="profile-row">
                <span className="label">Browser:</span>
                <span>{navigator.userAgent.split(" ").slice(-2).join(" ")}</span>
              </div>
              <div className="profile-row">
                <span className="label">Platform:</span>
                <span>{navigator.platform}</span>
              </div>
            </div>
            
            <div style={{ marginTop: "20px" }}>
              <button 
                className="btn-secondary" 
                onClick={() => {
                  if (window.confirm("Are you sure you want to logout?")) {
                    localStorage.clear();
                    window.location.href = "/login";
                  }
                }}
                style={{ width: "100%", background: "#ef4444", color: "white" }}
              >
                🚪 Logout from all devices
              </button>
            </div>
          </div>
        </div>
        
        <style jsx>{`
          .profile-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
            gap: 24px;
            margin-top: 24px;
          }
          
          .profile-card {
            background: white;
            border-radius: 16px;
            padding: 24px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          
          .profile-card h2 {
            margin: 0 0 20px 0;
            font-size: 20px;
            color: #1e293b;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 10px;
          }
          
          .profile-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            border-bottom: 1px solid #f1f5f9;
          }
          
          .profile-row .label {
            font-weight: 600;
            color: #64748b;
          }
          
          .profile-row span:last-child {
            color: #1e293b;
          }
          
          .btn-primary, .btn-secondary, .btn-save {
            padding: 10px 20px;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s;
          }
          
          .btn-primary {
            background: linear-gradient(135deg, #4361ee 0%, #764ba2 100%);
            color: white;
          }
          
          .btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(67, 97, 238, 0.3);
          }
          
          .btn-secondary {
            background: #f1f5f9;
            color: #64748b;
          }
          
          .btn-secondary:hover {
            background: #e2e8f0;
          }
          
          .btn-save {
            background: #10b981;
            color: white;
          }
          
          .btn-save:hover {
            background: #059669;
            transform: translateY(-2px);
          }
        `}</style>
      </div>
    </div>
  );
}